import { useState, useEffect } from 'react';
import CategoryGrid from '../../components/Category/CategoryGrid';
import EventList from '../../components/Event/EventList';
import useAuthStatus from '../../hooks/useAuthStatus';
import './Admin.css';

import { db } from '../../firebase';
import { collection, addDoc, onSnapshot, Timestamp } from 'firebase/firestore';

function getDateObj(date) {
  if (!date) return null;
  if (typeof date.toDate === 'function') {
    return date.toDate();
  }
  const d = new Date(date);
  return isNaN(d) ? null : d;
}

function AdminEventsView() {
  const [categories, setCategories] = useState([]);
  const [events, setEvents] = useState([]);
  const { user } = useAuthStatus();

  const [newCategory, setNewCategory] = useState({ name: '', image: '' });
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    rsvpBy: '',
    description: '',
    category: '',
    image: '',
    location: '',
    maximumNumberOfAttendees: '',
  });

  // Real-time listener for categories
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        setCategories(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      }
    );
    return () => unsubscribe();
  }, []);

  // Real-time listener for events
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'events'),
      (snapshot) => {
        setEvents(snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      }
    );
    return () => unsubscribe();
  }, []);

  const handleAddCategory = async e => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.image) return;
    try {
      await addDoc(collection(db, 'categories'), {
        name: newCategory.name,
        image: newCategory.image
      });
      setNewCategory({ name: '', image: '' });
    } catch (err) {
      alert('Failed to add category: ' + err.message);
    }
  };

  const handleAddEvent = async e => {
    e.preventDefault();
    if (
      !newEvent.name ||
      !newEvent.date ||
      !newEvent.description ||
      !newEvent.category ||
      !newEvent.image ||
      !newEvent.location ||
      !newEvent.maximumNumberOfAttendees
    ) return;
    try {
      await addDoc(collection(db, 'events'), {
        attendees: [],
        date: Timestamp.fromDate(new Date(newEvent.date)),
        category: newEvent.category,
        description: newEvent.description,
        image: newEvent.image,
        location: newEvent.location,
        rsvpBy: newEvent.rsvpBy ? Timestamp.fromDate(new Date(newEvent.rsvpBy)) : null,
        maximumNumberOfAttendees: Number(newEvent.maximumNumberOfAttendees),
        name: newEvent.name,
      });
      setNewEvent({
        name: '',
        date: '',
        category: '',
        description: '',
        image: '',
        location: '',
        maximumNumberOfAttendees: '',
      });
    } catch (err) {
      alert('Failed to add event: ' + err.message);
    }
  };

  const sortedEvents = [...events].sort((a, b) => {
    const dateA = getDateObj(a.date);
    const dateB = getDateObj(b.date);
    if (!dateA || !dateB) return 0;
    return dateB - dateA;
  });

  return (
    <div className="admin-events-view">
      <div className="admin-add-section">
        <h2>Add New Category</h2>
        <form onSubmit={handleAddCategory} className="admin-form">
          <input
            type="text"
            placeholder="Category Name"
            value={newCategory.name}
            onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Image URL (e.g. https://imgur.com/yourimage.jpg)"
            value={newCategory.image}
            onChange={e => setNewCategory({ ...newCategory, image: e.target.value })}
            required
          />
          {newCategory.image && (
            <img src={newCategory.image} alt="Preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
          )}
          <button type="submit">Add Category</button>
        </form>

        <h2>Add New Event</h2>
        <form onSubmit={handleAddEvent} className="admin-form">
          <input
            type="text"
            placeholder="Event Name"
            value={newEvent.name}
            onChange={e => setNewEvent({ ...newEvent, name: e.target.value })}
            required
          />
          <input
            type="date"
            placeholder="Event Date"
            value={newEvent.date}
            onChange={e => setNewEvent({ ...newEvent, date: e.target.value })}
            required
          />
          <input
            type="date"
            placeholder="RSVP By"
            value={newEvent.rsvpBy}
            onChange={e => setNewEvent({ ...newEvent, rsvpBy: e.target.value })}
          />
          <input
            type="text"
            placeholder="Location"
            value={newEvent.location}
            onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
            required
          />
          <input
            type="number"
            placeholder="Maximum Number of Attendees"
            value={newEvent.maximumNumberOfAttendees}
            onChange={e => setNewEvent({ ...newEvent, maximumNumberOfAttendees: e.target.value })}
            required
            min={1}
          />
          <input
            type="text"
            placeholder="Image URL (e.g. https://imgur.com/yourevent.jpg)"
            value={newEvent.image}
            onChange={e => setNewEvent({ ...newEvent, image: e.target.value })}
            required
          />
          {newEvent.image && (
            <img src={newEvent.image} alt="Preview" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }} />
          )}
          <input
            type="text"
            placeholder="Description"
            value={newEvent.description}
            onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
            required
          />
          <select
            value={newEvent.category}
            onChange={e => setNewEvent({ ...newEvent, category: e.target.value })}
            required
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
          <button type="submit">Add Event</button>
        </form>
      </div>

      <div className="admin-category-section">
        <h2>Categories</h2>
        <CategoryGrid categories={categories} />
      </div>

      <div className="admin-event-section">
        <h2>Events</h2>
        <EventList events={sortedEvents} user={user} />
      </div>
    </div>
  );
}

export default AdminEventsView;